(() => {
  "use strict";

  const SHOWCASE_SELECTOR = "[data-jm-showcase]";
  const SIDE_SELECTOR = "[data-jm-showcase-side]";
  const ITEM_SELECTOR = "[data-jm-showcase-item]";
  const CENTER_SELECTOR = "[data-jm-showcase-center]";
  const HIDDEN_CLASS = "is-hidden-by-space";
  const OVERLAP_GAP = 4;

  function parseCssNumber(value, fallback) {
    const parsed = Number.parseFloat(String(value || "").trim());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function readMinItemWidth(showcase) {
    const css = getComputedStyle(showcase);
    return parseCssNumber(css.getPropertyValue("--jm-showcase-item-min-width"), 120);
  }

  function readSideGap(side) {
    const css = getComputedStyle(side);
    return parseCssNumber(css.columnGap || css.gap, 0);
  }

  function isVisibleItem(item) {
    return !item.classList.contains(HIDDEN_CLASS);
  }

  function visibleItems(side) {
    return Array.from(side.querySelectorAll(ITEM_SELECTOR)).filter(isVisibleItem);
  }

  function resetItems(showcase) {
    showcase.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      item.classList.remove(HIDDEN_CLASS);
      item.removeAttribute("aria-hidden");
      item.removeAttribute("tabindex");
    });
  }

  function hideItem(item) {
    item.classList.add(HIDDEN_CLASS);
    item.setAttribute("aria-hidden", "true");
    item.setAttribute("tabindex", "-1");
  }

  function revealShowcase(showcase) {
    if (showcase.dataset.jmShowcaseReady === "true") return;
    showcase.dataset.jmShowcaseReady = "true";
  }

  function allImagesComplete(showcase) {
    return Array.from(showcase.querySelectorAll("img")).every((img) => img.complete);
  }

  function hasSideWidthCollision(side, minWidth, gap) {
    const items = visibleItems(side);
    if (!items.length) return false;

    const availableWidth = side.clientWidth;
    const estimatedItemWidth = (availableWidth - gap * (items.length - 1)) / items.length;
    return estimatedItemWidth < minWidth;
  }

  function requiredWidthForFullHeight(item, fallbackWidth) {
    const image = item.querySelector("img");
    if (!image || !image.naturalWidth || !image.naturalHeight) {
      return fallbackWidth;
    }

    const ratio = image.naturalWidth / image.naturalHeight;
    if (!Number.isFinite(ratio) || ratio <= 0) {
      return fallbackWidth;
    }

    return Math.max(item.clientHeight * ratio, fallbackWidth);
  }

  function hasImageFitCollision(side, minWidth) {
    const items = visibleItems(side);
    if (!items.length) return false;

    for (const item of items) {
      const requiredWidth = requiredWidthForFullHeight(item, minWidth);
      if (item.clientWidth + 0.5 < requiredWidth) return true;
    }

    return false;
  }

  function hasCenterCollision(showcase, left, right, center) {
    if (!center) return false;

    const centerRect = center.getBoundingClientRect();
    const leftItems = visibleItems(left);
    const rightItems = visibleItems(right);

    for (const item of leftItems) {
      const rect = item.getBoundingClientRect();
      if (rect.right > centerRect.left - OVERLAP_GAP) return true;
    }

    for (const item of rightItems) {
      const rect = item.getBoundingClientRect();
      if (rect.left < centerRect.right + OVERLAP_GAP) return true;
    }

    return false;
  }

  function pickItemToHide(showcase, left, right) {
    const leftItems = visibleItems(left);
    const rightItems = visibleItems(right);
    if (!leftItems.length && !rightItems.length) return null;

    let sideToHide = "right";
    if (leftItems.length > rightItems.length) {
      sideToHide = "left";
    } else if (rightItems.length > leftItems.length) {
      sideToHide = "right";
    } else {
      sideToHide = showcase.dataset.jmHideNextSide || "right";
      showcase.dataset.jmHideNextSide = sideToHide === "right" ? "left" : "right";
    }

    if (sideToHide === "left") {
      return leftItems[leftItems.length - 1] || rightItems[0] || null;
    }
    return rightItems[0] || leftItems[leftItems.length - 1] || null;
  }

  function rebalance(showcase) {
    const sides = showcase.querySelectorAll(SIDE_SELECTOR);
    if (sides.length < 2) return;

    const left = showcase.querySelector('[data-jm-showcase-side="left"]');
    const right = showcase.querySelector('[data-jm-showcase-side="right"]');
    const center = showcase.querySelector(CENTER_SELECTOR);
    if (!left || !right) return;

    resetItems(showcase);
    showcase.dataset.jmHideNextSide = "right";

    const minWidth = readMinItemWidth(showcase);
    const leftGap = readSideGap(left);
    const rightGap = readSideGap(right);

    let guard = showcase.querySelectorAll(ITEM_SELECTOR).length + 8;
    while (guard > 0) {
      const hasCollision =
        hasSideWidthCollision(left, minWidth, leftGap) ||
        hasSideWidthCollision(right, minWidth, rightGap) ||
        hasImageFitCollision(left, minWidth) ||
        hasImageFitCollision(right, minWidth) ||
        hasCenterCollision(showcase, left, right, center);
      if (!hasCollision) break;

      const itemToHide = pickItemToHide(showcase, left, right);
      if (!itemToHide) break;
      hideItem(itemToHide);
      guard -= 1;
    }

    if (allImagesComplete(showcase)) {
      revealShowcase(showcase);
    }
  }

  function attachObservers(showcase) {
    if (showcase.dataset.jmShowcaseInit === "1") {
      rebalance(showcase);
      return;
    }

    showcase.dataset.jmShowcaseInit = "1";
    showcase.dataset.jmShowcaseReady = "false";

    let rafId = 0;
    const schedule = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        rebalance(showcase);
      });
    };

    const observer = new ResizeObserver(schedule);
    observer.observe(showcase);
    showcase.querySelectorAll(SIDE_SELECTOR).forEach((side) => observer.observe(side));
    const center = showcase.querySelector(CENTER_SELECTOR);
    if (center) observer.observe(center);

    const onImageSettled = () => {
      if (allImagesComplete(showcase)) {
        revealShowcase(showcase);
      }
      schedule();
    };

    showcase.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", onImageSettled, { once: true });
        img.addEventListener("error", onImageSettled, { once: true });
      }
    });

    showcase._jmShowcaseObserver = observer;
    schedule();
    if (!showcase.querySelector("img") || allImagesComplete(showcase)) {
      revealShowcase(showcase);
    }
  }

  function initAll(root = document) {
    root.querySelectorAll(SHOWCASE_SELECTOR).forEach(attachObservers);
  }

  function setupMutationObserver() {
    /*
     * Scope observer to the product-grid container instead of
     * documentElement. The showcase only gets (re-)inserted when the
     * grid AJAX-reloads, so watching the entire document fires the
     * callback on every wishlist toggle, swatch hover, quick-view
     * open, etc. — thousands of wasted invocations per session.
     * Falls back to body only if the container is missing.
     */
    const root = document.getElementById("productgridcontainer") || document.body;
    if (!root) return;

    let rafId = 0;
    const observer = new MutationObserver((mutations) => {
      const hasShowcaseNode = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches?.(SHOWCASE_SELECTOR) || node.querySelector?.(SHOWCASE_SELECTOR);
        })
      );
      if (!hasShowcaseNode) return;

      if (rafId) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => initAll(document));
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(document));
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  setupMutationObserver();
})();
